import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ethers } from "ethers";
import { useAccount, useConnect, useNetwork, useSwitchNetwork } from "wagmi";

import { TRDX_CHAIN_ID } from "../../services/web3/trdxContractConfig";
import {
  getSwitchToNetworkLabel,
  getTxExplorerUrl,
  getWrongNetworkMessage,
} from "../../config/chainEnv";
import { formatEthRateLabel } from "../../services/web3/trdxSaleDisplay";
import {
  isTrdxReadRpcExplicitlyConfigured,
  isTrdxSaleEnvExplicitlyConfigured,
} from "../../services/web3/trdxSaleEnv";
import {
  getTrdxNetworkLabel,
  quoteEthToTrdx,
  quoteTrdxToEth,
  readEthBalance,
  readPaused,
  readSaleInventory,
  readTokensPerEth,
  readTrdxBalance,
  submitBuyTrdxWithEth,
} from "../../services/web3/trdxSaleService";
import { syncTrdxWalletPurchase } from "../../services/trdxWalletPurchaseApi";
import {
  clearPendingTrdxPurchase,
  getPendingTrdxPurchase,
  setPendingTrdxPurchase,
} from "../../utils/pendingTrdxPurchase";
import { trackCheckoutStarted, trackProductViewed } from "../../services/analyticsClient";
import AddTrdxToMetaMaskButton from "./AddTrdxToMetaMaskButton";
import "./trdx-purchase-modal.css";

const TRDX_DECIMALS = 18;
const ONRAMP_RETURN_FLAG = "treydora_onramp_return";

function truncateAddress(addr) {
  const s = String(addr || "");
  if (s.length < 12) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

const logSafeWeb3Error = (label, error) => {
  // eslint-disable-next-line no-console
  console.warn(`[TRDX ${label}]`, {
    code: error?.code,
    reason: error?.reason,
    message: error?.message,
  });
};

const normalizeEthBuyError = (error) => {
  const raw = error?.reason || error?.message || "";
  const lowered = String(raw).toLowerCase();

  if (lowered.includes("user rejected") || lowered.includes("user denied")) {
    return "Purchase was rejected or cancelled in your wallet.";
  }
  if (lowered.includes("wrong network") || lowered.includes("switch to")) {
    return getWrongNetworkMessage(TRDX_CHAIN_ID);
  }
  if (lowered.includes("paused") || lowered.includes("whennotpaused")) {
    return "The sale is paused. Try again later.";
  }
  if (lowered.includes("insufficient funds")) {
    return "Insufficient ETH for this purchase and gas.";
  }
  if (lowered.includes("insufficient inventory") || lowered.includes("insufficientinventory")) {
    return "Sale inventory is too low for this purchase.";
  }
  if (lowered.includes("slippage")) {
    return "Price moved beyond the allowed minimum. Try again with a smaller amount.";
  }
  if (lowered.includes("cannot estimate gas") || lowered.includes("execution reverted")) {
    return "Purchase could not be completed. Check your balance and that the sale is active.";
  }
  return "Purchase failed. Please try again.";
};

/**
 * @param {{ isOpen: boolean, onClose?: () => void, saleSnapshot?: object, onFundWalletEth?: () => void }} props
 */
const TrdxPurchaseModal = ({ isOpen, onClose, saleSnapshot, onFundWalletEth }) => {
  const { address, isConnected, isConnecting: isConnectingWallet } = useAccount();
  const { connect, connectors } = useConnect();
  const { chain } = useNetwork();
  const { switchNetwork, isLoading: isSwitchingNetwork } = useSwitchNetwork();

  const wrongNetwork = Boolean(isConnected && chain?.id && Number(chain.id) !== TRDX_CHAIN_ID);

  const [trdxAmount, setTrdxAmount] = useState("");

  const [salePaused, setSalePaused] = useState(false);
  const [inventoryWei, setInventoryWei] = useState(null);
  const [tokensPerEthWei, setTokensPerEthWei] = useState(null);
  const [saleReadFailed, setSaleReadFailed] = useState(false);
  const [trdxBalance, setTrdxBalance] = useState(null);
  const [ethBalanceWei, setEthBalanceWei] = useState(null);
  const [ethBalanceLoading, setEthBalanceLoading] = useState(false);

  const [quoteEthRequiredWei, setQuoteEthRequiredWei] = useState(null);
  const [quoteEthOut, setQuoteEthOut] = useState(null);

  const [banner, setBanner] = useState({ kind: "", text: "" });
  const [lastEthTxHash, setLastEthTxHash] = useState("");
  const [purchasePhase, setPurchasePhase] = useState("idle");
  const [onrampReturnRefresh, setOnrampReturnRefresh] = useState(false);
  const [syncPhase, setSyncPhase] = useState("idle");

  const [busy, setBusy] = useState({
    quoteEth: false,
    buyEth: false,
    refresh: false,
    sync: false,
  });

  const resumedPendingRef = useRef(false);
  const clearBanner = useCallback(() => setBanner({ kind: "", text: "" }), []);

  const envConfigured = isTrdxSaleEnvExplicitlyConfigured();
  const parentPurchaseEnabled = saleSnapshot?.walletPurchaseEnabled ?? false;

  const refreshEthBalance = useCallback(async () => {
    if (!address || wrongNetwork) {
      setEthBalanceWei(null);
      return;
    }
    setEthBalanceLoading(true);
    try {
      const bal = await readEthBalance(address);
      setEthBalanceWei(bal);
    } catch {
      setEthBalanceWei(null);
    } finally {
      setEthBalanceLoading(false);
    }
  }, [address, wrongNetwork]);

  const refreshLiveData = useCallback(async () => {
    setBusy((b) => ({ ...b, refresh: true }));
    if (!envConfigured) {
      setSalePaused(false);
      setInventoryWei(null);
      setTokensPerEthWei(null);
      setSaleReadFailed(true);
      setBusy((b) => ({ ...b, refresh: false }));
      return;
    }
    try {
      const [paused, inv, tpe] = await Promise.all([
        readPaused(),
        readSaleInventory(),
        readTokensPerEth(),
      ]);
      setSalePaused(Boolean(paused));
      setInventoryWei(inv);
      setTokensPerEthWei(tpe);
      setSaleReadFailed(false);
    } catch (err) {
      logSafeWeb3Error("sale contract read", err);
      setInventoryWei(null);
      setTokensPerEthWei(null);
      setSaleReadFailed(true);
    }

    if (!address) {
      setTrdxBalance(null);
      setEthBalanceWei(null);
      setBusy((b) => ({ ...b, refresh: false }));
      return;
    }

    try {
      const trdx = await readTrdxBalance(address);
      setTrdxBalance(trdx);
    } catch {
      setTrdxBalance(null);
    }

    await refreshEthBalance();

    setBusy((b) => ({ ...b, refresh: false }));
  }, [address, envConfigured, refreshEthBalance]);

  const runBackendSync = useCallback(
    async (txHash, phaseAfter = "confirmed") => {
      if (!txHash || !address) return;
      setSyncPhase("pending");
      setBusy((b) => ({ ...b, sync: true }));
      try {
        await syncTrdxWalletPurchase({
          txHash,
          walletAddress: address,
          chainId: TRDX_CHAIN_ID,
        });
        setSyncPhase("ok");
        setPendingTrdxPurchase({
          txHash,
          walletAddress: address,
          chainId: TRDX_CHAIN_ID,
          phase: "confirmed",
        });
        clearPendingTrdxPurchase();
        setPurchasePhase(phaseAfter);
        setBanner({
          kind: "success",
          text: "Purchase confirmed on-chain. TRDX has been sent to your wallet.",
        });
      } catch (err) {
        if (err?.code === "TRDX_SYNC_NOT_CONFIGURED" || err?.status === 503) {
          setSyncPhase("skipped");
          clearPendingTrdxPurchase();
          setPurchasePhase(phaseAfter);
          setBanner({
            kind: "success",
            text: "Purchase confirmed on-chain. TRDX has been sent to your wallet.",
          });
          return;
        }
        setSyncPhase("failed");
        setPendingTrdxPurchase({
          txHash,
          walletAddress: address,
          chainId: TRDX_CHAIN_ID,
          phase: "sync_failed",
        });
        setPurchasePhase("sync_failed");
        setBanner({
          kind: "error",
          text: "Purchase confirmed on-chain, but dashboard sync failed. Use Retry Dashboard Sync — do not buy again.",
        });
      } finally {
        setBusy((b) => ({ ...b, sync: false }));
      }
    },
    [address]
  );

  const resumePendingPurchase = useCallback(async () => {
    const pending = getPendingTrdxPurchase();
    if (!pending?.txHash || !address) return;
    if (
      pending.walletAddress &&
      String(pending.walletAddress).toLowerCase() !== String(address).toLowerCase()
    ) {
      return;
    }
    setLastEthTxHash(pending.txHash);
    if (pending.phase === "sync_failed") {
      setPurchasePhase("sync_failed");
      setSyncPhase("failed");
      return;
    }
    setPurchasePhase("confirming");
    setBusy((b) => ({ ...b, buyEth: true }));
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const receipt = await provider.waitForTransaction(pending.txHash, 1);
      if (receipt?.status === 1) {
        await runBackendSync(pending.txHash, "confirmed");
      } else {
        setPurchasePhase("failed");
        clearPendingTrdxPurchase();
        setBanner({ kind: "error", text: "Previous transaction failed on-chain." });
      }
    } catch {
      setPurchasePhase("submitted");
    } finally {
      setBusy((b) => ({ ...b, buyEth: false }));
    }
  }, [address, runBackendSync]);

  useEffect(() => {
    if (!isOpen) return undefined;
    trackProductViewed("trdx_sale", "modal", { view: "purchase_page" });
    clearBanner();
    setPurchasePhase("idle");
    setOnrampReturnRefresh(false);
    setSyncPhase("idle");
    setLastEthTxHash("");
    resumedPendingRef.current = false;
    refreshLiveData();

    try {
      if (sessionStorage.getItem(ONRAMP_RETURN_FLAG) === "1") {
        sessionStorage.removeItem(ONRAMP_RETURN_FLAG);
        setOnrampReturnRefresh(true);
        setBanner({
          kind: "info",
          text: "ETH funding may take a few minutes. Once it appears in your wallet, continue your purchase below.",
        });
        refreshEthBalance().finally(() => setOnrampReturnRefresh(false));
      }
    } catch {
      /* noop */
    }

    const id = setInterval(refreshLiveData, 45_000);
    return () => clearInterval(id);
  }, [isOpen, refreshLiveData, clearBanner, refreshEthBalance]);

  useEffect(() => {
    if (!isOpen || !isConnected || wrongNetwork) return;
    if (resumedPendingRef.current) return;
    const pending = getPendingTrdxPurchase();
    if (pending?.txHash) {
      resumedPendingRef.current = true;
      resumePendingPurchase();
    }
  }, [isOpen, isConnected, wrongNetwork, resumePendingPurchase]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    const run = async () => {
      const trimmed = trdxAmount.trim();
      if (!trimmed) {
        setQuoteEthRequiredWei(null);
        setQuoteEthOut(null);
        return;
      }
      try {
        const trdxWei = ethers.utils.parseUnits(trimmed, TRDX_DECIMALS);
        if (trdxWei.isZero()) {
          setQuoteEthRequiredWei(null);
          setQuoteEthOut(null);
          return;
        }
        setBusy((b) => ({ ...b, quoteEth: true }));
        const tpe = tokensPerEthWei || (await readTokensPerEth());
        const ethWei = await quoteTrdxToEth(trdxWei, tpe);
        const out = await quoteEthToTrdx(ethWei);
        if (!cancelled) {
          setQuoteEthRequiredWei(ethWei);
          setQuoteEthOut(out);
        }
      } catch {
        if (!cancelled) {
          setQuoteEthRequiredWei(null);
          setQuoteEthOut(null);
        }
      } finally {
        if (!cancelled) setBusy((b) => ({ ...b, quoteEth: false }));
      }
    };
    const t = setTimeout(run, 320);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [trdxAmount, isOpen, tokensPerEthWei]);

  const trdxWeiParsed = useMemo(() => {
    try {
      const t = trdxAmount.trim();
      if (!t) return null;
      return ethers.utils.parseUnits(t, TRDX_DECIMALS);
    } catch {
      return null;
    }
  }, [trdxAmount]);

  const minOutFromEth = useMemo(() => {
    if (!quoteEthOut || quoteEthOut.isZero()) return null;
    return quoteEthOut.mul(99).div(100);
  }, [quoteEthOut]);

  const insufficientEth = useMemo(() => {
    if (!quoteEthRequiredWei || quoteEthRequiredWei.isZero()) return false;
    if (ethBalanceWei == null) return false;
    return ethBalanceWei.lt(quoteEthRequiredWei);
  }, [quoteEthRequiredWei, ethBalanceWei]);

  const ethBalLabel =
    ethBalanceLoading
      ? "Reading…"
      : ethBalanceWei != null
        ? `${ethers.utils.formatEther(ethBalanceWei)} ETH`
        : isConnected
          ? "—"
          : "—";

  const onConnect = async () => {
    clearBanner();
    if (!connectors?.length) {
      setBanner({ kind: "error", text: "Install MetaMask or another browser wallet to continue." });
      return;
    }
    try {
      await connect({ connector: connectors[0] });
    } catch (err) {
      logSafeWeb3Error("connect", err);
      setBanner({ kind: "error", text: "Wallet connection was cancelled." });
    }
  };

  const onSwitchNetwork = async () => {
    clearBanner();
    if (!switchNetwork) {
      setBanner({ kind: "error", text: getWrongNetworkMessage(TRDX_CHAIN_ID) });
      return;
    }
    try {
      await switchNetwork(TRDX_CHAIN_ID);
    } catch (err) {
      logSafeWeb3Error("switch network", err);
      setBanner({ kind: "error", text: "Network switch was cancelled or failed in your wallet." });
    }
  };

  const handleBuyEth = async () => {
    clearBanner();
    if (!isConnected) {
      setBanner({ kind: "error", text: "Wallet not connected." });
      return;
    }
    if (wrongNetwork) {
      setBanner({ kind: "error", text: getWrongNetworkMessage(TRDX_CHAIN_ID) });
      return;
    }
    if (salePaused) {
      setBanner({ kind: "error", text: "Sale is paused." });
      return;
    }
    if (!trdxWeiParsed || trdxWeiParsed.isZero()) {
      setBanner({ kind: "error", text: "Enter a valid TRDX amount." });
      return;
    }
    if (!quoteEthRequiredWei || !minOutFromEth) {
      setBanner({ kind: "error", text: "Could not calculate required ETH for this amount." });
      return;
    }
    if (insufficientEth) {
      setBanner({ kind: "error", text: "Insufficient ETH. Fund your wallet or reduce the TRDX amount." });
      return;
    }

    setBusy((b) => ({ ...b, buyEth: true }));
    trackCheckoutStarted({ productType: "trdx_wallet", paymentMethod: "crypto" });
    setPurchasePhase("preparing");
    let submitted = null;
    try {
      setPurchasePhase("wallet_pending");
      submitted = await submitBuyTrdxWithEth(quoteEthRequiredWei, minOutFromEth);
      setLastEthTxHash(submitted.txHash);
      setPurchasePhase("submitted");
      setPendingTrdxPurchase({
        txHash: submitted.txHash,
        walletAddress: address,
        chainId: TRDX_CHAIN_ID,
        trdxAmount: trdxAmount.trim(),
        ethAmountWei: quoteEthRequiredWei.toString(),
        phase: "submitted",
      });
      setPurchasePhase("confirming");
      const receipt = await submitted.wait();
      if (receipt?.status !== 1) {
        throw new Error("Transaction failed on-chain.");
      }
      await runBackendSync(submitted.txHash, "confirmed");
      await refreshLiveData();
    } catch (err) {
      logSafeWeb3Error("ETH buy", err);
      const msg = normalizeEthBuyError(err);
      if (submitted?.txHash) {
        setPurchasePhase("submitted");
      } else {
        clearPendingTrdxPurchase();
        setPurchasePhase("idle");
      }
      setBanner({ kind: "error", text: msg });
    } finally {
      setBusy((b) => ({ ...b, buyEth: false }));
    }
  };

  const handleRetrySync = async () => {
    const hash = lastEthTxHash || getPendingTrdxPurchase()?.txHash;
    if (!hash) return;
    await runBackendSync(hash, "confirmed");
  };

  const inventoryLabel = (() => {
    if (!envConfigured) return "—";
    if (inventoryWei != null) {
      return `${ethers.utils.formatUnits(inventoryWei, TRDX_DECIMALS)} TRDX`;
    }
    if (saleReadFailed) return isConnected ? "Unavailable" : "—";
    if (busy.refresh) return "Loading…";
    return "—";
  })();

  if (!isOpen) return null;

  const trdxBalLabel =
    trdxBalance != null ? `${ethers.utils.formatUnits(trdxBalance, TRDX_DECIMALS)} TRDX` : "—";

  const requiredEthLabel =
    quoteEthRequiredWei != null && !quoteEthRequiredWei.isZero()
      ? `${ethers.utils.formatEther(quoteEthRequiredWei)} ETH`
      : "—";

  const hasInventory = Boolean(inventoryWei && !inventoryWei.isZero());
  const walletOnSaleChain = !wrongNetwork;
  const saleOperational =
    envConfigured && !saleReadFailed && !salePaused && hasInventory && parentPurchaseEnabled;
  const canBuyOnChain = saleOperational && walletOnSaleChain && !busy.buyEth && purchasePhase === "idle";

  const ethRateLabel =
    saleReadFailed && tokensPerEthWei == null
      ? "—"
      : formatEthRateLabel(tokensPerEthWei ?? saleSnapshot?.tokensPerEthWei);

  const readRpcConfigured = isTrdxReadRpcExplicitlyConfigured();

  const purchaseErrorBanner = !envConfigured
    ? "TRDX sale is not configured for this environment. Set REACT_APP_TRDX_* variables before enabling purchases."
    : saleReadFailed && isConnected
      ? readRpcConfigured
        ? "Live TRDX sale data could not be loaded. Purchases are disabled until sale data loads."
        : "Live TRDX sale data could not be loaded. Set REACT_APP_TRDX_RPC_URL on this site for reliable reads."
      : !hasInventory && envConfigured && !saleReadFailed
        ? "Sale inventory is empty. Purchases are temporarily unavailable."
        : salePaused && envConfigured && !saleReadFailed
          ? "Sale is paused. Purchases are temporarily unavailable."
          : "";

  const infoBanner =
    !isConnected && (!envConfigured || saleReadFailed)
      ? "Connect your wallet to load live sale details and purchase TREYDOREX."
      : "";

  const connectLabel = isConnectingWallet ? "Connecting…" : "Connect Wallet";
  const purchaseConfirmed = purchasePhase === "confirmed" || syncPhase === "ok";

  return (
    <div
      className="trdx-purchase-modal__backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="trdx-purchase-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="trdx-purchase-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="trdx-purchase-modal__header">
          <div>
            <h2 id="trdx-purchase-title" className="trdx-purchase-modal__title">
              Buy TREYDOREX Token
            </h2>
            <p className="trdx-purchase-modal__subtitle">
              {getTrdxNetworkLabel()} · On-chain wallet purchase · Requires ETH in your wallet
            </p>
          </div>
          <button
            type="button"
            className="trdx-purchase-modal__close"
            aria-label="Close"
            onClick={() => onClose?.()}
          >
            ×
          </button>
        </header>

        <div className="trdx-purchase-modal__body">
          {purchaseConfirmed ? (
            <div className="trdx-purchase-modal__success-block" role="status">
              <p className="trdx-purchase-modal__success-title">Purchase confirmed</p>
              <p className="trdx-purchase-modal__hint trdx-purchase-modal__hint--muted">
                TRDX has been sent to your connected wallet.
              </p>
              {isConnected && envConfigured ? (
                <AddTrdxToMetaMaskButton className="trdx-purchase-modal__metamask-import trdx-purchase-modal__metamask-import--success" />
              ) : null}
            </div>
          ) : null}

          <div className="trdx-purchase-modal__status-grid">
            <div>
              <strong>Network:</strong> {getTrdxNetworkLabel()}
            </div>
            <div>
              <strong>Wallet:</strong>{" "}
              {isConnected ? (
                <span className="trdx-purchase-modal__mono">{truncateAddress(address)}</span>
              ) : (
                "Not connected"
              )}
            </div>
            <div>
              <strong>Inventory:</strong> {inventoryLabel}
            </div>
            <div>
              <strong>ETH rate:</strong> {ethRateLabel}
            </div>
            <div>
              <strong>Your TRDX:</strong> {isConnected ? trdxBalLabel : "—"}
            </div>
            {isConnected && walletOnSaleChain ? (
              <div>
                <strong>ETH balance:</strong> {ethBalLabel}
              </div>
            ) : null}
          </div>

          {isConnected && envConfigured && !purchaseConfirmed ? (
            <AddTrdxToMetaMaskButton className="trdx-purchase-modal__metamask-import" />
          ) : null}

          {infoBanner ? (
            <div className="trdx-purchase-modal__banner trdx-purchase-modal__banner--info" role="status">
              {infoBanner}
            </div>
          ) : null}

          {purchaseErrorBanner ? (
            <div className="trdx-purchase-modal__banner trdx-purchase-modal__banner--error" role="status">
              {purchaseErrorBanner}
            </div>
          ) : null}

          {banner.text ? (
            <div
              className={`trdx-purchase-modal__banner trdx-purchase-modal__banner--${
                banner.kind === "success" ? "success" : banner.kind === "error" ? "error" : "info"
              }`}
              role="status"
            >
              {banner.text}
            </div>
          ) : null}

          {purchasePhase === "wallet_pending" ? (
            <p className="trdx-purchase-modal__hint" role="status">
              Confirm the transaction in your wallet…
            </p>
          ) : null}
          {purchasePhase === "submitted" || purchasePhase === "confirming" ? (
            <p className="trdx-purchase-modal__hint" role="status">
              Transaction submitted — waiting for confirmation…
            </p>
          ) : null}
          {onrampReturnRefresh ? (
            <p className="trdx-purchase-modal__hint" role="status">
              Refreshing ETH balance after funding…
            </p>
          ) : null}
          {syncPhase === "pending" ? (
            <p className="trdx-purchase-modal__hint" role="status">
              Syncing purchase to dashboard…
            </p>
          ) : null}

          {lastEthTxHash ? (
            <p className="trdx-purchase-modal__tx">
              Tx:{" "}
              <a href={getTxExplorerUrl(TRDX_CHAIN_ID, lastEthTxHash) || "#"} target="_blank" rel="noopener noreferrer">
                {lastEthTxHash.slice(0, 10)}…{lastEthTxHash.slice(-8)}
              </a>
            </p>
          ) : null}

          {!isConnected ? (
            <button
              type="button"
              className="trdx-purchase-modal__btn trdx-purchase-modal__btn--gold trdx-purchase-modal__btn--block"
              onClick={onConnect}
              disabled={isConnectingWallet}
            >
              {connectLabel}
            </button>
          ) : null}

          {isConnected && wrongNetwork ? (
            <button
              type="button"
              className="trdx-purchase-modal__btn trdx-purchase-modal__btn--blue trdx-purchase-modal__btn--block"
              onClick={onSwitchNetwork}
              disabled={isSwitchingNetwork}
            >
              {isSwitchingNetwork ? "Switching…" : getSwitchToNetworkLabel(TRDX_CHAIN_ID)}
            </button>
          ) : null}

          {isConnected && walletOnSaleChain && !purchaseConfirmed ? (
            <div className="trdx-purchase-modal__purchase-form">
              <label className="trdx-purchase-modal__field">
                <span className="trdx-purchase-modal__label">TRDX amount</span>
                <input
                  className="trdx-purchase-modal__input"
                  inputMode="decimal"
                  autoComplete="off"
                  placeholder="100"
                  value={trdxAmount}
                  onChange={(e) => setTrdxAmount(e.target.value)}
                  disabled={busy.buyEth}
                />
              </label>
              <p className="trdx-purchase-modal__hint">
                Required ETH: <strong>{requiredEthLabel}</strong>
              </p>
              {insufficientEth && trdxAmount.trim() ? (
                <p className="trdx-purchase-modal__hint trdx-purchase-modal__hint--warn" role="status">
                  Insufficient ETH for this purchase (excluding gas).
                </p>
              ) : null}

              <button
                type="button"
                className="trdx-purchase-modal__btn trdx-purchase-modal__btn--blue trdx-purchase-modal__btn--block"
                onClick={handleBuyEth}
                disabled={
                  !canBuyOnChain ||
                  !trdxWeiParsed ||
                  trdxWeiParsed.isZero() ||
                  !minOutFromEth ||
                  insufficientEth
                }
              >
                {busy.buyEth
                  ? purchasePhase === "wallet_pending"
                    ? "Confirm in wallet…"
                    : purchasePhase === "confirming"
                      ? "Confirming…"
                      : "Processing…"
                  : "Buy TREYDOREX with Wallet"}
              </button>

              {purchasePhase === "sync_failed" ? (
                <button
                  type="button"
                  className="trdx-purchase-modal__btn trdx-purchase-modal__btn--gold trdx-purchase-modal__btn--block"
                  onClick={handleRetrySync}
                  disabled={busy.sync}
                >
                  {busy.sync ? "Syncing…" : "Retry Dashboard Sync"}
                </button>
              ) : null}
            </div>
          ) : null}

          {onFundWalletEth ? (
            <button
              type="button"
              className="trdx-purchase-modal__btn trdx-purchase-modal__btn--ghost trdx-purchase-modal__btn--block trdx-purchase-modal__fund-helper"
              onClick={() => onFundWalletEth()}
            >
              Need ETH first? Fund Wallet with ETH
            </button>
          ) : null}

          <button
            type="button"
            className="trdx-purchase-modal__btn trdx-purchase-modal__btn--ghost trdx-purchase-modal__btn--block"
            onClick={() => onClose?.()}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrdxPurchaseModal;
